import { ComponentRef, DestroyRef, Directive, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BaseAppStateService } from '@modules/common/services/base-app-state.service';
import { Application } from '@modules/common/types/application';
import { BaseVirtualFolder } from '@modules/common/types/base-virtual-folder';
import { Stitch } from '@modules/common/types/stitch';
import { DpSearchLandingService } from '@modules/dynamic-panel/services/dp-search-landing.service';
import { DynamicPanelService } from '@modules/dynamic-panel/services/dynamic-panel.service';
import { ContextMenuDirective } from '@frontendlib/components/features/popover/directives/context-menu.directive';
import type { PopoverPlacement } from '@frontendlib/components/features/popover/types/placement';
import type { PopoverTrigger } from '@frontendlib/components/features/popover/types/trigger';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';
import { StateService } from '@modules/settings/services/state.service';
import { StateKey } from '@modules/settings/types/state-key';
import { merge } from 'rxjs';

export interface DPContextMenuComponent<VirtualFolder> {
  filterKey: VirtualFolder;
  change: EventEmitter<VirtualFolder>;
}

@Directive({
  selector: '[appDPContextMenu]',
  standalone: false,
})
export abstract class DPContextMenuDirective<VirtualFolder, C extends DPContextMenuComponent<VirtualFolder>>
  extends ContextMenuDirective
  implements OnInit
{
  @Input()
  public override stchPopoverTrigger: PopoverTrigger = 'click';
  @Input()
  public override stchPopoverPlacement: PopoverPlacement = 'bottomLeft';

  @Output()
  public appDPContextMenuOpened = this.contextMenuOpened;
  @Output()
  public appDPContextMenuFilterChanged = new EventEmitter<VirtualFolder>();

  protected abstract readonly stateKey: StateKey;
  protected abstract readonly application: Application;
  protected filterKey: VirtualFolder;

  protected readonly destroyRef = inject(DestroyRef);
  protected readonly stateService: StateService = inject(StateService);
  protected readonly searchService = inject(AdvancedSearchService);
  protected readonly appStateService =
    inject<BaseAppStateService<Stitch, Stitch, BaseVirtualFolder>>(BaseAppStateService);
  protected readonly dpService = inject(DynamicPanelService);
  protected readonly searchLandingService = inject(DpSearchLandingService);

  protected override registerInstance(componentRef: ComponentRef<C>) {
    componentRef.instance.filterKey = this.filterKey;
    componentRef.instance.change.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((filterKey: VirtualFolder) => {
      this.dpService.clearIdsSearchMode();
      componentRef.instance.filterKey = filterKey;
      this.stateService.setState(this.stateKey, filterKey);
    });
  }

  public override ngOnInit(): void {
    super.ngOnInit();

    this.stateService
      .getState(this.stateKey)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((filterKey: VirtualFolder) => {
        this.appDPContextMenuFilterChanged.emit(filterKey);
        this.filterKey = filterKey;
      });

    // `merge`, not `combineLatest`, and both values read synchronously inside the handler: the folder
    // changes *because* the search state changed, so a cached pair always holds one value from before the
    // change. Reading both live makes the reset below independent of the order these subscriptions happen
    // to be registered in — the sidebar has the same pair, where getting it wrong costs the landing.
    let wasSearchApplied = this.searchService.getStateSync().isAdvancedSearchApplied(this.application);
    merge(this.appStateService.getVirtualFolder(), this.searchService.getState())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const isAdvancedSearchApplied = this.searchService.getStateSync().isAdvancedSearchApplied(this.application);

        // A live ids search counts as applied (isAdvancedSearchApplied returns true on `ids`), so
        // the ids-search flag outliving the applied state means the ids were just CLEARED (the
        // search pill's x). Drop the stale flag, or it blocks the reset below forever and strands
        // the panel on an empty "All Results" no folder list even shows. Only on the
        // applied→cleared edge: while a pending searchByIds is still being consumed, the flag
        // legitimately precedes the published state and must survive these emissions.
        if (wasSearchApplied && !isAdvancedSearchApplied && this.dpService.isIdsSearchMode()) {
          this.dpService.clearIdsSearchMode();
        }
        wasSearchApplied = isAdvancedSearchApplied;

        const folder = this.appStateService.getVirtualFolderSync();
        const idsSearchMode = this.dpService.isIdsSearchMode();

        if (!isAdvancedSearchApplied && folder === this.appStateService.searchResultsFolder && !idsSearchMode) {
          this.appStateService.setDefaultVirtualFolder();
        } else {
          this.stateService.setState(this.stateKey, folder);
        }
      });

    // The panel's own folder list, so it lands on "All Results" the same way the sidebar does. Read from
    // `DpSearchLandingService` rather than subscribed straight from `AdvancedSearchService`: this directive
    // is unmounted whenever a form is open in the panel, which is exactly when a search started from a knot
    // chip on that form arrives. The pending read is what collects such a search on remount; see that
    // service for why a direct subscription cannot.
    //
    // Recorded and still applied, not just recorded: the search can have gone away while we were unmounted
    // — the user cleared it, or switched the panel's app, which resets it. Landing on "All Results" with
    // nothing applied would trip the reset above and drop the user on the app default, losing the folder
    // they had. The record is claimed either way, so a search that has gone cannot relocate a later mount.
    if (this.searchLandingService.consumePending() && this.searchService.getStateSync().isApplied()) {
      this.appStateService.setSearchResultsFolder();
    }

    this.searchLandingService
      .onLanding()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.searchLandingService.clearPending();
        this.appStateService.setSearchResultsFolder();
      });
  }
}
